import { create } from 'zustand';
import { isTv } from '@/lib/native.ts';
import type { Channel } from '@shared/types.ts';

/**
 * Three presentation tiers, following the model Plex uses.
 *
 *   browse      grid visible, video playing as a corner PiP
 *   theater     video fills the browser viewport; browser chrome still there
 *   fullscreen  real Fullscreen API — no browser chrome, no taskbar
 *
 * Clicking a channel goes to `theater`. A button escalates to `fullscreen`.
 * Back/Escape steps DOWN one tier at a time: fullscreen → theater → browse.
 *
 * All of this is presentation. The <video> element never unmounts, so no
 * transition here ever interrupts playback — stepping back to `browse` leaves
 * the picture running in the corner.
 */
export type ViewTier = 'browse' | 'theater' | 'fullscreen';

interface UiState {
  view: ViewTier;
  /** The element that goes into real fullscreen. */
  shell: HTMLElement | null;
  /** The recently-watched strip, opened with UP over video. */
  recentsOpen: boolean;
  /**
   * Where channel-cycling is HEADING while the remote is still being pressed.
   *
   * Holding right walks the lineup faster than a stream can start, so the
   * tune is debounced and this is what the chrome shows in the meantime —
   * names flick past the way they do on a set-top box, and exactly one stream
   * actually gets opened.
   */
  zapping: Channel | null;
  setView(v: ViewTier): void;
  /** Step down one tier. Returns the tier moved to. */
  stepBack(): ViewTier;
  setShell(el: HTMLElement | null): void;
  setRecentsOpen(open: boolean): void;
  setZapping(channel: Channel | null): void;
}

export const useUi = create<UiState>((set, get) => ({
  view: 'browse',
  shell: null,
  recentsOpen: false,
  zapping: null,
  setView: (view) => set({ view, recentsOpen: view === 'browse' ? false : get().recentsOpen }),
  setRecentsOpen: (recentsOpen) => set({ recentsOpen }),
  setZapping: (zapping) => set({ zapping }),
  /*
   * ONE STEP ON A TELEVISION, TWO IN A BROWSER.
   *
   * The three tiers are a browser idea and a real one there: browse, theater
   * and fullscreen are three actual sizes of the same video, and stepping
   * down through them is what a person means by "back".
   *
   * On Android TV they are one size. The picture comes from ExoPlayer on a
   * surface that already fills the screen, so "fullscreen" and "theater" look
   * identical — and a BACK that appears to do nothing, because it moved
   * between two states that render the same, is the most confusing kind of
   * unresponsive. There, back out of playing means back to browsing.
   */
  stepBack: () => {
    const next: ViewTier = isTv() || get().view === 'theater' ? 'browse'
      : get().view === 'fullscreen' ? 'theater'
      : 'browse';
    set({ view: next, recentsOpen: false });
    return next;
  },
  setShell: (shell) => set({ shell }),
}));
